/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';

import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import type { Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { ImageFragment, VideoFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import PlayIcon from 'bundles/ent-website/assets/PlayIcon';
import Image from 'bundles/ent-website/contentful/core/Image';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';

type PropsFromCaller = {
  image: ImageFragment;
  video: VideoFragment;
  maxWidth?: number | string;
  maxHeight?: number | string;
};

type PropsFromContext = {
  _eventData: TrackingContextData;
};

type Props = PropsFromCaller & PropsFromContext;

const styles: Record<string, Interpolation<Theme>> = {
  videoContent: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
  `,
  playIcon: css`
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: -45px;
    margin-top: -45px;
  `,

  playMask: css`
    position: absolute;
    background-color: rgb(0 0 0 / 25%);
    transition: background-color 0.2s ease-in-out;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &:hover {
      background: rgb(0 0 0 / 50%);
    }
  `,

  container: css`
    height: 100%;
    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  videoWrapper: css`
    position: relative;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
  `,
  overlayImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
};

export function Video({ image, video, maxWidth, maxHeight, _eventData, ...otherProps }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const track = useTracker();
  const [playButtonPressed, setPlayButtonPressed] = React.useState(false);
  if (!image.url) {
    return null;
  }

  const handleOpenVideoPlayer = () => {
    const videoTrackingName = eventingTransform(`Video_${video.sys.spaceId}_${video.sys.id}`);
    track('interact_video_player', {
      videoPlayerInteractionType: 'play',
      videoPlayer: {
        videoId: video.sys.id,
        videoName: videoTrackingName,
      },
      contentful: {
        spaceId: video.sys.spaceId,
        environmentId: video.sys.environmentId,
      },
    });
    Retracked.trackComponent(
      _eventData,
      {
        id: video.sys.id,
        spaceId: video.sys.spaceId,
        environmentId: video.sys.environmentId,
      },
      videoTrackingName,
      'play'
    );
    setPlayButtonPressed(true);
    videoRef?.current?.play();
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      handleOpenVideoPlayer();
    }
  };

  return (
    <>
      <div css={styles.container}>
        <div css={styles.videoWrapper}>
          {/* Opacity 0 until user clicked custom overlay below */}
          <div data-testid="video_content" css={[styles.videoContent, { opacity: playButtonPressed ? 1 : 0 }]}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video tabIndex={playButtonPressed ? 0 : -1} controls width="100%" controlsList="nodownload" ref={videoRef}>
              {video.url && <source src={video.url} type={video.contentType ?? undefined} />}
            </video>
          </div>
          {/* Overlay with custom play button */}
          {!playButtonPressed && (
            <>
              <Image
                data-testid="Video-Image"
                content={image}
                width={image.width ?? undefined}
                height={image.height ?? undefined}
                css={[
                  {
                    maxWidth,
                    maxHeight,
                  },
                  styles.overlayImage,
                ]}
                {...otherProps}
              />
              <div
                role="button"
                css={styles.playMask}
                tabIndex={0}
                onClick={handleOpenVideoPlayer}
                onKeyDown={handleKeyDown}
                data-testid="Video-mask"
              >
                <PlayIcon css={styles.playIcon} data-testid="Video-PlayIcon" />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default compose<Props, PropsFromCaller>(
  getContext({
    _eventData: PropTypes.object,
  })
)(Video);
